import {createTheme, responsiveFontSizes, SimplePaletteColorOptions, Theme} from "@mui/material/styles";
import {brand, fontFamilies, ModeTokens, modeTokens, radii, ThemeMode, zIndex} from "./tokens";
import {resolveBrandRoles} from "./brandRoles";

/**
 * Central MUI theme definition for transferify-ui — v3 design system edition.
 *
 * Light and dark themes are built from the shared v3 token layer (`./tokens.ts`, extracted from the
 * design source). The app extends the default palette with brand-specific colors (`upgradeColor`,
 * `soonColor`, …) and the shape with an extra-large border radius; those legacy extension fields are
 * kept working (declared via module augmentation below) but remapped onto v3 values so every
 * pre-v3 screen keeps compiling and rendering with the new palette.
 *
 * The active theme object is shared with `styled-components` (see `src/theme/styled.d.ts` and
 * `src/theme/AppTheme.tsx`), so the styled-components `DefaultTheme` is augmented to be the MUI
 * `Theme`, and the raw token set travels with the theme as `theme.tokens`.
 */

/** Brand palette colors layered on top of MUI's SimplePaletteColorOptions. */
export interface ColorOptions extends SimplePaletteColorOptions {
	upgradeColor: string;
	soonColor: string;
	upgradeColorDark: string;
	branded: string;
}

// --- MUI theme augmentation: teach MUI about our custom palette + shape + token fields. ---
// `@mui/material/styles` re-exports both PaletteColor and Shape, so augmenting it merges into the same
// interfaces the theme is built from — giving `theme.palette.primary.upgradeColor`,
// `theme.shape.xlBorderRadius` and `theme.tokens.*` full type-safety everywhere (incl.
// styled-components via styled.d.ts).
declare module "@mui/material/styles" {
	interface PaletteColor extends ColorOptions {
	}

	interface SimplePaletteColorOptions {
		upgradeColor?: string;
		soonColor?: string;
		upgradeColorDark?: string;
		branded?: string;
	}

	interface Shape {
		xlBorderRadius: string;
	}

	interface Theme {
		/** The raw v3 per-mode design tokens the theme was built from. */
		tokens: ModeTokens;
	}

	interface ThemeOptions {
		tokens?: ModeTokens;
	}
}

/**
 * Primary = the filled-button role: v3 brand blue, or the account colour shaded until it reads (ui#2935, ui#2982).
 * Legacy extension fields remapped onto the v3 palette.
 */
function buildPrimaryOptions(tokens: ModeTokens): ColorOptions {
	return {
		main: tokens.actionFill,
		dark: tokens.actionFillHover,
		contrastText: tokens.actionInk,
		// Upgrade/PRO accents: the pastel blue pair (ui#837), which replaced gold on the upgrade and
		// sign-in CTAs it names. `brand.gold` is a separate family and still paints its own surfaces
		// and buttons elsewhere — reading this pair as "all CTAs" is what made the old comment wrong.
		upgradeColor: brand.upgrade,
		upgradeColorDark: brand.upgradeDark,
		// "Soon" accents moved from the old violet to the per-mode v3 violet.
		soonColor: tokens.violet,
		// `branded` has always been the form-field surface color (BaseInput/BaseSelect/dialogs).
		branded: tokens.field,
	};
}

/** Secondary = v3 signal teal; `branded` keeps the deep-navy tooltip surface. */
function buildSecondaryOptions(tokens: ModeTokens): ColorOptions {
	return {
		main: tokens.teal,
		upgradeColor: brand.upgrade,
		upgradeColorDark: brand.upgradeDark,
		soonColor: tokens.violet,
		branded: brand.navy,
	};
}

/**
 * Builds the v3 MUI theme for one mode from the shared token layer. `brandColor` is the signed-in
 * account's colour (already gated on the plan, `account/brand/accountAppColor`): each element role takes
 * it, or the nearest shade of it that clears that role's contrast floor (`resolveBrandRoles`,
 * kitecorp/transferify-ui#2935, kitecorp/transferify-ui#2982). `null` builds exactly the unbranded theme.
 */
export function createV3Theme(mode: ThemeMode, brandColor: string | null = null): Theme {
	const tokens: ModeTokens = {...modeTokens[mode], ...resolveBrandRoles(brandColor, modeTokens[mode])};
	const built = createTheme({
		palette: {
			mode,
			primary: buildPrimaryOptions(tokens),
			secondary: buildSecondaryOptions(tokens),
			error: {main: brand.error},
			warning: {main: brand.warning},
			success: {main: tokens.teal},
			info: {main: tokens.blue},
			background: {
				default: tokens.paper,
				paper: tokens.card,
			},
			text: {
				primary: tokens.ink,
				secondary: tokens.ink2,
				disabled: tokens.ink3,
			},
			divider: tokens.line,
			// Used by `getContrastText()` to maximize the contrast between background and text.
			contrastThreshold: 3,
			// Shift a color's luminance by ~2 indexes within its tonal palette.
			tonalOffset: 0.2,
		},
		typography: {
			fontFamily: fontFamilies.sans,
			// v3 buttons are sentence-case, weight 600 (design `.abtn`/`.cta`).
			button: {
				fontWeight: 600,
				textTransform: "none",
			},
		},
		// `createTheme`'s `shape` option is typed `Partial<Shape>` from @mui/system, whose internal
		// sub-path is not augmentable under bundler resolution. The READ side IS fully typed via the
		// @mui/material/styles `Shape` augmentation above (consumers read theme.shape.xlBorderRadius with
		// types); only this input literal needs a cast so the custom field is carried onto the theme.
		shape: {
			// v3 control radius (design `.abtn` et al.); cards keep the larger xlBorderRadius.
			borderRadius: 12,
			xlBorderRadius: radii.card,
		} as any,
		// Point MUI's own overlay layers at the shared token ladder (`tokens.zIndex`) so MUI's
		// Dialog/Modal and Snackbar stack consistently with the token-driven overlays (DropOverlay,
		// Banner, Toast). Only these two keys are remapped; MUI's other z-indexes (appBar, drawer,
		// tooltip…) keep their defaults so MUI's internal ordering (e.g. tooltip above modal) is not
		// disturbed. `createTheme` deep-merges this onto the default `zIndex` scale.
		zIndex: {
			modal: zIndex.modal,
			snackbar: zIndex.toast,
		},
		// Every switch renders the SECONDARY (signal teal) track when on (owner, ui#823). A theme
		// default rather than per-site `color` props, so present and future MUI switches agree with
		// the custom styled Switch in OptionsSheet.styles — one decision, two rendering stacks.
		components: {
			MuiSwitch: {
				defaultProps: {color: "secondary"},
				// The on-track is the toggle ROLE (ui#2935): today's teal, which is `secondary.main`, or the
				// account colour shaded until it reads. Painted here rather than through the palette so a brand
				// colour moves every switch without also moving every other `secondary` surface.
				styleOverrides: {
					switchBase: {
						"&.Mui-checked": {color: tokens.toggleOn},
						"&.Mui-checked + .MuiSwitch-track": {backgroundColor: tokens.toggleOn},
					},
				},
			},
			// A text or outlined primary button is a LINK-weight label rather than a fill, so it takes the
			// link role and its 4.5:1 floor; `primary.main` is the fill role and only has to clear 3:1. The
			// outline follows the label at the half strength MUI gives it, so one button never shows two colours.
			MuiButton: {
				variants: [
					{props: {variant: "text", color: "primary"}, style: {color: tokens.linkInk}},
					{props: {variant: "outlined", color: "primary"}, style: {color: tokens.linkInk, borderColor: `color-mix(in srgb, ${tokens.linkInk} 50%, transparent)`}},
				],
			},
		},
		tokens,
	});
	return responsiveFontSizes(built);
}

export const lightTheme: Theme = createV3Theme("light");
export const darkTheme: Theme = createV3Theme("dark");

/** One built theme per mode and account colour: a sign-in or a mode toggle must not rebuild MUI's theme. */
const brandedThemes = new Map<string, Theme>();

/** The theme for this mode and account colour; `null` is the shared unbranded theme. */
export function themeFor(mode: ThemeMode, brandColor: string | null): Theme {
	if (brandColor === null) {
		return mode === "dark" ? darkTheme : lightTheme;
	}
	const key = `${mode}:${brandColor}`;
	let built = brandedThemes.get(key);
	if (built === undefined) {
		built = createV3Theme(mode, brandColor);
		brandedThemes.set(key, built);
	}
	return built;
}

/**
 * Backwards-compatible default export: the light theme. Pre-v3 code imports `theme` directly;
 * mode-aware code should render under `AppTheme` / `ThemeModeProvider` instead and read the active
 * theme from context.
 */
const theme: Theme = lightTheme;

/**
 * Backwards-compatible alias. Pre-migration the custom theme type was `SuperTheme`; with MUI module
 * augmentation the standard `Theme` already carries the brand palette/shape, so they are equivalent.
 */
export type SuperTheme = Theme;

export {theme};
